import AxiosInstance from '@/api/axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export interface ClarionCallCounts {
  inbound: number;
  outbound: number;
  web: number;
  /** Sum of the three above, so the four cards add up on screen. */
  total: number;
}

export type CallChannel = 'inboundcall' | 'outboundcall' | 'webcall';

export interface ClarionCallRow {
  /** Needed by the delete action, which reuses DELETE /leads/:leadId. */
  _id: string;
  sender: string;
  /** 'inbound' | 'outbound' | 'web' */
  direction: string;
  name: string;
  phone: string;
  email: string;
  branchName: string;
  created_date: string;
  /**
   * Seconds, from clarion_voice_session matched by sender. null when the call
   * has no session (made before sessions were logged, or not logged).
   */
  call_duration: number | null;
  /** The clarion_voice_session id; null when the call has no session. */
  session_id: string | null;
}

/** Everything about one Clarion call, for the call details pop-up. */
export interface ClarionCallDetail {
  _id: string;
  call_id: string | null;
  sender: string | null;
  /** 'inbound' | 'outbound' | 'web' */
  direction: string;
  channel: string | null;
  call_status: string | null;
  agent_name: string | null;
  branchName: string | null;
  name: string;
  phone: string;
  email: string;
  started_at: string | null;
  ended_at: string | null;
  created_date: string | null;
  /** Seconds. */
  call_duration: number | null;
  transfer_requested: boolean;
  success_transfer: boolean;
  transfer_outcome: string | null;
  transfer_reason: string | null;
  transfer_mode: string | null;
  transfer_to: string | null;
  transfer_summary: string | null;
  booking_made: boolean;
  booking_rescheduled: boolean;
  callback_requested: boolean;
  opt_out: boolean;
  /** Signed, ~15 minutes. null when the call has no recording. */
  recording_url: string | null;
  transcript: { role: 'human' | 'ai'; message: string; at: string | null }[];
  timezone: string | null;
}

export interface ClarionPagination {
  currentPage: number;
  perpage: number;
  total: number;
  totalPages: number;
}

interface FetchArgs {
  organization_id?: string;
  branch_id?: string | null;
  region_id?: string | null;
  /** The canonical "YYYY-MM-DD - YYYY-MM-DD" string from generalFilterSlice. */
  date_range?: string;
}

const hasValue = (val?: string | null) =>
  Boolean(val && val.trim() !== '' && val !== 'all');

export const fetchClarionCallCounts = createAsyncThunk(
  'clarionCalls/fetch',
  async (
    { organization_id, branch_id, region_id, date_range }: FetchArgs,
    thunkAPI
  ) => {
    // Same guard as the control centre: with neither a branch nor a region the
    // backend's access middleware refuses the request for scoped roles, so
    // asking would only produce a 403.
    const hasBranch = hasValue(branch_id);
    const hasRegion = hasValue(region_id);
    if (!hasBranch && !hasRegion) return null;

    try {
      const response = await AxiosInstance.get('/agents/call-counts', {
        params: {
          organization_id,
          branch_id,
          region_id,
          date_range,
        },
        signal: thunkAPI.signal,
      });
      if (response.data.success) return response.data.data as ClarionCallCounts;
      return null;
    } catch (error: any) {
      if (thunkAPI.signal.aborted || error.code === 'ERR_CANCELED') {
        return thunkAPI.rejectWithValue('ABORTED');
      }
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error?.message || 'Request failed'
      );
    }
  }
);

interface FetchListArgs extends FetchArgs {
  channel?: CallChannel | null;
  page?: number;
  perpage?: number;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

export const fetchClarionCallList = createAsyncThunk(
  'clarionCalls/fetchList',
  async (args: FetchListArgs, thunkAPI) => {
    const { organization_id, branch_id, region_id, date_range } = args;

    // Same scope guard as the counts: without a branch or region the backend's
    // access middleware refuses the request for scoped roles.
    if (!hasValue(branch_id) && !hasValue(region_id)) return null;

    try {
      const response = await AxiosInstance.get('/agents/call-list', {
        params: {
          organization_id,
          branch_id,
          region_id,
          date_range,
          channel: args.channel || undefined,
          page: args.page ?? 1,
          perpage: args.perpage ?? 10,
          search: args.search || undefined,
          sort: args.sort || undefined,
          order: args.order || undefined,
        },
        signal: thunkAPI.signal,
      });
      if (response.data.success) return response.data.data;
      return null;
    } catch (error: any) {
      if (thunkAPI.signal.aborted || error.code === 'ERR_CANCELED') {
        return thunkAPI.rejectWithValue('ABORTED');
      }
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error?.message || 'Request failed'
      );
    }
  }
);

/** Totals from clarion_voice_session (one document per call). */
export interface ClarionSessionSummary {
  sessions: number;
  /** success_transfer === true: the transfer actually connected. */
  transferred: number;
  /** transfer_requested === true: the caller asked to be transferred. */
  transferRequested: number;
  /** transferRequested split by call type. */
  transferRequestedByType: { inbound: number; outbound: number; web: number };
  sessionSeconds: number;
  sessionHours: number;
  /** Session time excluding connected transfers: "Hours Handled by Clarion". */
  handledSeconds: number;
  handledHours: number;
}

/** One voice session for a caller, with a short-lived recording link. */
export interface ClarionSessionRecording {
  call_id: string;
  call_type: string | null;
  started_at: string | null;
  ended_at: string | null;
  call_duration: number | null;
  success_transfer: boolean;
  transfer_requested: boolean;
  /** Signed, ~15 minutes. null when the session has no recording. */
  recording_url: string | null;
}

export const fetchClarionSessionSummary = createAsyncThunk(
  'clarionCalls/fetchSessionSummary',
  async (
    { organization_id, branch_id, region_id, date_range }: FetchArgs,
    thunkAPI
  ) => {
    // Same scope guard as the call counts: scoped roles are refused without a
    // branch or region, so asking would only produce a 403.
    if (!hasValue(branch_id) && !hasValue(region_id)) return null;

    try {
      const response = await AxiosInstance.get('/agents/call-session-summary', {
        params: { organization_id, branch_id, region_id, date_range },
        signal: thunkAPI.signal,
      });
      if (response.data.success)
        return response.data.data as ClarionSessionSummary;
      return null;
    } catch (error: any) {
      if (thunkAPI.signal.aborted || error.code === 'ERR_CANCELED') {
        return thunkAPI.rejectWithValue('ABORTED');
      }
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error?.message || 'Request failed'
      );
    }
  }
);

interface ClarionCallsState {
  data: ClarionCallCounts | null;
  loading: boolean;
  error: string | null;
  /** requestId of the in-flight fetch; older ones are ignored. */
  currentRequestId: string | null;
  rows: ClarionCallRow[];
  pagination: ClarionPagination;
  timezone: string | null;
  listLoading: boolean;
  /** requestId of the in-flight list fetch; older ones are ignored. */
  listRequestId: string | null;
  sessionSummary: ClarionSessionSummary | null;
  sessionLoading: boolean;
  /** requestId of the in-flight session-summary fetch. */
  sessionRequestId: string | null;
}

const emptyPagination: ClarionPagination = {
  currentPage: 1,
  perpage: 10,
  total: 0,
  totalPages: 1,
};

const initialState: ClarionCallsState = {
  data: null,
  loading: false,
  error: null,
  currentRequestId: null,
  rows: [],
  pagination: emptyPagination,
  timezone: null,
  listLoading: false,
  listRequestId: null,
  sessionSummary: null,
  sessionLoading: false,
  sessionRequestId: null,
};

const clarionCallsSlice = createSlice({
  name: 'clarionCalls',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchClarionCallCounts.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchClarionCallCounts.fulfilled, (state, action) => {
        // A response from a superseded request must not overwrite newer data —
        // changing the date range twice quickly would otherwise land the older
        // numbers last.
        if (state.currentRequestId !== action.meta.requestId) return;
        state.currentRequestId = null;
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchClarionCallList.pending, (state, action) => {
        state.listLoading = true;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(fetchClarionCallList.fulfilled, (state, action) => {
        // Superseded responses must not overwrite newer rows — changing the
        // channel twice quickly would otherwise land the older page last.
        if (state.listRequestId !== action.meta.requestId) return;
        state.listRequestId = null;
        state.listLoading = false;
        state.rows = action.payload?.data ?? [];
        state.pagination = action.payload?.pagination ?? emptyPagination;
        state.timezone = action.payload?.timezone ?? null;
      })
      .addCase(fetchClarionCallList.rejected, (state, action) => {
        if (action.payload === 'ABORTED') return;
        if (state.listRequestId !== action.meta.requestId) return;
        state.listRequestId = null;
        state.listLoading = false;
        state.rows = [];
        state.pagination = emptyPagination;
      })
      .addCase(fetchClarionSessionSummary.pending, (state, action) => {
        state.sessionLoading = true;
        state.sessionRequestId = action.meta.requestId;
      })
      .addCase(fetchClarionSessionSummary.fulfilled, (state, action) => {
        // A superseded response must not overwrite newer totals.
        if (state.sessionRequestId !== action.meta.requestId) return;
        state.sessionRequestId = null;
        state.sessionLoading = false;
        state.sessionSummary = action.payload;
      })
      .addCase(fetchClarionSessionSummary.rejected, (state, action) => {
        if (action.payload === 'ABORTED') return;
        if (state.sessionRequestId !== action.meta.requestId) return;
        state.sessionRequestId = null;
        state.sessionLoading = false;
        state.sessionSummary = null;
      })
      .addCase(fetchClarionCallCounts.rejected, (state, action) => {
        if (action.payload === 'ABORTED') return;
        if (state.currentRequestId !== action.meta.requestId) return;
        state.currentRequestId = null;
        state.loading = false;
        state.data = null;
        state.error =
          (action.payload as string) || 'Failed to load call counts';
      });
  },
});

export default clarionCallsSlice.reducer;
